import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MatchVideoModelDto, PreanalysisQuestionModelDto, ProcessingJobModelDto, VisionEventModelDto } from '../api/models';
import { CoreProvider } from '../core.provider';

/** Statuses where something is happening and the list should keep refreshing itself. */
export const LIVE_STATUSES = ['uploading', 'queued', 'preanalysis', 'processing'] as const;

/** How long a presigned video URL is good for, minus a margin. Must stay under the API's 3h. */
export const PRESIGNED_VIDEO_EXPIRATION_MS = 2.5 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class MatchVideosService {
  private core!: CoreProvider;

  readonly videos$ = new BehaviorSubject<MatchVideoModelDto[]>([]);
  readonly loading$ = new BehaviorSubject<boolean>(false);

  private videoUrlCache = new Map<string, { url: string; at: number }>();
  private pollHandle: any;

  async initCore(core: CoreProvider) {
    this.core = core;
  }

  //#region Listing

  async refresh(): Promise<MatchVideoModelDto[]> {
    this.loading$.next(true);
    try {
      const videos = await firstValueFrom(this.core.api.matchVideo.matchVideoControllerGetMatchVideos({}));
      this.videos$.next(videos);
      return videos;
    } finally {
      this.loading$.next(false);
    }
  }

  /**
   * Polls while anything is in flight and stops on its own once nothing is.
   * A match takes minutes, so 5s is frequent enough to feel live without
   * hammering the API for an hour.
   */
  startPollingWhileBusy(intervalMs = 5000) {
    this.stopPolling();
    const tick = async () => {
      const videos = await this.refresh().catch(() => null);
      if (!videos || !videos.some((v) => (LIVE_STATUSES as readonly string[]).includes(v.status ?? ''))) this.stopPolling();
    };
    this.pollHandle = setInterval(() => void tick(), intervalMs);
    void tick();
  }

  stopPolling() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  //#endregion

  async get(uuid: string): Promise<MatchVideoModelDto> {
    return firstValueFrom(this.core.api.matchVideo.matchVideoControllerGetMatchVideo({ uuid }));
  }

  async create(body: Partial<MatchVideoModelDto> & { title: string }): Promise<MatchVideoModelDto> {
    return firstValueFrom(this.core.api.matchVideo.matchVideoControllerCreateMatchVideo({ body: body as MatchVideoModelDto }));
  }

  /**
   * Edits the match's own details — its title, competition and team names.
   *
   * The cached list is patched with what comes back rather than re-fetched: the
   * caller is usually a screen that is already showing the video, and a full
   * refresh there would replace every row while the user is reading one.
   */
  async update(video: MatchVideoModelDto): Promise<MatchVideoModelDto> {
    const saved = await firstValueFrom(
      this.core.api.matchVideo.matchVideoControllerUpdateMatchVideo({ uuid: video.uuid!, body: video }),
    );
    this.videos$.next(this.videos$.value.map((v) => (v.uuid === saved.uuid ? saved : v)));
    return saved;
  }

  async remove(uuid: string): Promise<void> {
    await firstValueFrom(this.core.api.matchVideo.matchVideoControllerDeleteMatchVideo({ uuid }));
    this.videos$.next(this.videos$.value.filter((v) => v.uuid !== uuid));
  }

  async analyze(uuid: string): Promise<ProcessingJobModelDto> {
    return firstValueFrom(this.core.api.matchVideo.matchVideoControllerAnalyze({ uuid }));
  }

  async cancel(uuid: string): Promise<ProcessingJobModelDto> {
    return firstValueFrom(this.core.api.matchVideo.matchVideoControllerCancel({ uuid }));
  }

  async job(uuid: string): Promise<ProcessingJobModelDto | null> {
    return firstValueFrom(this.core.api.matchVideo.matchVideoControllerGetJob({ uuid })).catch(() => null);
  }

  //#region Preanalysis

  async questions(uuid: string): Promise<PreanalysisQuestionModelDto[]> {
    return firstValueFrom(this.core.api.preanalysis.preanalysisControllerGetQuestions({ uuid }));
  }

  async answer(uuid: string, answers: { questionUuid: string; value: unknown }[]): Promise<ProcessingJobModelDto> {
    return firstValueFrom(
      this.core.api.preanalysis.preanalysisControllerSubmitAnswers({
        uuid,
        // The API stores answers as JSON strings so a new question type needs no
        // schema change; the encoding boundary lives here and nowhere else.
        body: { answers: answers.map((a) => ({ questionUuid: a.questionUuid, value: JSON.stringify(a.value) })) },
      }),
    );
  }

  //#endregion

  //#region Events

  async events(uuid: string): Promise<VisionEventModelDto[]> {
    return firstValueFrom(this.core.api.visionEvents.visionEventsControllerGetEvents({ uuid }));
  }

  async updateEvent(uuid: string, event: VisionEventModelDto): Promise<VisionEventModelDto> {
    return firstValueFrom(
      this.core.api.visionEvents.visionEventsControllerUpdateEvent({ uuid, eventUuid: event.uuid!, body: event }),
    );
  }

  async deleteEvent(uuid: string, eventUuid: string): Promise<void> {
    await firstValueFrom(this.core.api.visionEvents.visionEventsControllerDeleteEvent({ uuid, eventUuid }));
  }

  async createEvent(uuid: string, event: VisionEventModelDto): Promise<VisionEventModelDto> {
    return firstValueFrom(this.core.api.visionEvents.visionEventsControllerCreateEvent({ uuid, body: event }));
  }

  //#endregion

  /**
   * A presigned URL for the whole match, cached until it is about to expire.
   *
   * The review player seeks into this one file instead of loading a clip per
   * event: one signed URL and one buffered video beats a hundred small ones,
   * and it is why the worker does not cut clips by default.
   */
  async videoUrl(uuid: string): Promise<string | null> {
    const cached = this.videoUrlCache.get(uuid);
    if (cached && Date.now() - cached.at < PRESIGNED_VIDEO_EXPIRATION_MS) return cached.url;

    const video = await this.get(uuid);
    if (!video.videoUrl) return null;
    this.videoUrlCache.set(uuid, { url: video.videoUrl, at: Date.now() });
    return video.videoUrl;
  }
}
